/**
 * Module documentation for the ERP Help system.
 * Each module contains sections with title, content, and optional flow diagram.
 * Section types: content (text), flow (diagram with steps array).
 */

export const MODULE_DOCS = [
  {
    id: 'flujos',
    title: 'Flujos de negocio',
    icon: 'GitBranch',
    modules: [
      {
        id: 'flujo-venta-pos',
        title: 'Flujo de venta en caja (POS)',
        path: '/pos',
        sections: [
          {
            title: 'Descripción',
            content: 'El flujo principal de venta en el punto de venta. El cajero debe tener caja abierta para registrar ventas. Cada venta descuenta automáticamente el stock.'
          },
          {
            title: 'Diagrama del flujo',
            flow: {
              steps: [
                { label: 'Cajero inicia turno', detail: 'Selecciona caja y monto inicial' },
                { label: 'Apertura de caja', detail: 'Sistema registra apertura_caja' },
                { label: 'Agregar productos', detail: 'Búsqueda por código o nombre' },
                { label: 'Aplicar promociones', detail: 'Opcional, descuentos automáticos' },
                { label: 'Procesar pago', detail: 'Efectivo y tarjetas (débito/crédito)' },
                { label: 'Venta registrada', detail: 'Stock descontado, boleta/factura' }
              ],
              layout: 'vertical'
            }
          }
        ]
      },
      {
        id: 'flujo-preventa-venta',
        title: 'Flujo Preventa → Venta',
        path: '/preventas',
        sections: [
          {
            title: 'Descripción',
            content: 'Flujo para ventas asistidas: el vendedor crea una preventa (carrito) y el cajero la carga en POS para cobrar. Útil cuando el cliente elige productos en piso y paga en caja.'
          },
          {
            title: 'Diagrama del flujo',
            flow: {
              steps: [
                { label: 'Vendedor crea preventa', detail: 'Rol Preventa en POS; escaneo VND obligatorio' },
                { label: 'Preventa pendiente', detail: 'Estado: pendiente (estado=true)' },
                { label: 'Cliente va a caja', detail: 'Cajero busca preventa por ID o escanea PRV' },
                { label: 'Carga preventa en POS', detail: 'Productos, promos y peso se cargan al carrito' },
                { label: 'Cliente paga', detail: 'Procesa pago como venta normal' },
                { label: 'Preventa → Venta', detail: 'Preventa cerrada (estado=false), venta registrada' }
              ],
              layout: 'vertical'
            }
          }
        ]
      },
      {
        id: 'flujo-venta-online',
        title: 'Flujo Venta Online (ecommerce)',
        path: '/ordenes-online',
        sections: [
          {
            title: 'Descripción',
            content: 'Las compras del sitio web (ventasonline) se registran en ecom_orden. Al confirmarse el pago, el sistema crea automáticamente una preventa con origen venta_online y descuenta stock de la bodega ecommerce. El equipo del ERP solo debe preparar y despachar — no volver a cobrar.'
          },
          {
            title: 'Diagrama del flujo',
            flow: {
              steps: [
                { label: 'Cliente compra en la web', detail: 'Checkout en supercolchagua.cl' },
                { label: 'Pago confirmado', detail: 'Estado ecom_orden: pagada' },
                { label: 'Preventa automática', detail: 'PRV generado con origen venta_online' },
                { label: 'Stock descontado', detail: 'Bodega ecommerce' },
                { label: 'Operador revisa en ERP', detail: 'Módulo Ventas Online' },
                { label: 'Despacho o retiro', detail: 'Marcar como despachada al entregar' }
              ],
              layout: 'vertical'
            }
          }
        ]
      },
      {
        id: 'flujo-caja',
        title: 'Flujo de apertura y cierre de caja',
        path: '/cajas',
        sections: [
          {
            title: 'Descripción',
            content: 'Cada cajero abre una caja al iniciar su turno y la cierra al terminar. Todas las ventas del turno quedan asociadas a esa apertura. El cierre requiere conciliar efectivo, transferencias y tarjetas.'
          },
          {
            title: 'Día operacional (turnos nocturnos)',
            content: 'El sistema usa "día operacional" en vez de fecha calendario para que los turnos que cruzan medianoche queden agrupados al día de apertura. Por defecto, eventos antes de las 04:00 se imputan al día anterior. Ejemplo: caja abierta lunes 08:00 y cerrada martes 03:00 → todo (apertura, ventas y cierre) queda registrado en el lunes operacional, no aparece partido en dos días.'
          },
          {
            title: 'Diagrama del flujo',
            flow: {
              steps: [
                { label: 'Inicio de turno', detail: 'Cajero selecciona caja física' },
                { label: 'Ingresa monto inicial', detail: 'Efectivo con que inicia' },
                { label: 'Apertura registrada', detail: 'apertura_caja con fecha_operacional + opened_at' },
                { label: 'Ventas del turno', detail: 'Heredan fecha_operacional de la apertura' },
                { label: 'Fin de turno', detail: 'Ingresa montos por medio de pago' },
                { label: 'Cierre y arqueo', detail: 'closed_at = ahora; fecha_cierre = día operacional' }
              ],
              layout: 'vertical'
            }
          },
          {
            title: 'Reportes diarios',
            content: 'En Reportes se muestran los totales agrupados por día operacional con desglose por medio de pago (efectivo / tarjeta / transferencia) y un listado de cierres de turno con horario real de apertura y cierre. Sesiones de más de 24 h aparecen marcadas como advertencia. Ambas tablas pueden exportarse a CSV.'
          }
        ]
      },
      {
        id: 'flujo-compra',
        title: 'Flujo de compra e ingreso de stock',
        path: '/compras',
        sections: [
          {
            title: 'Descripción',
            content: 'Al registrar una compra a proveedor, el stock se incrementa automáticamente en la bodega seleccionada. Los productos con fecha de vencimiento generan lotes (producto_lotes).'
          },
          {
            title: 'Diagrama del flujo',
            flow: {
              steps: [
                { label: 'Crear orden de compra', detail: 'Selecciona proveedor y bodega' },
                { label: 'Agregar productos', detail: 'Cantidad, costo, fecha vencimiento' },
                { label: 'Registrar compra', detail: 'detalle_compra por producto' },
                { label: 'Crear lotes', detail: 'producto_lotes si tiene vencimiento' },
                { label: 'Actualizar stock', detail: 'stock.cantidad += cantidad' },
                { label: 'Actualizar costo (PPP)', detail: 'promedio ponderado; precio de venta queda pendiente' }
              ],
              layout: 'vertical'
            }
          }
        ]
      },
      {
        id: 'flujo-inventario',
        title: 'Flujo de inventario físico',
        path: '/inventarios',
        sections: [
          {
            title: 'Descripción',
            content: 'Conteo físico para conciliar el stock real con el sistema. Se crea un inventario por bodega, se registra el conteo por producto y al finalizar se ajustan las diferencias.'
          },
          {
            title: 'Diagrama del flujo',
            flow: {
              steps: [
                { label: 'Crear inventario', detail: 'Selecciona sucursal y bodega' },
                { label: 'Snapshot inicial', detail: 'Sistema copia stock actual' },
                { label: 'Conteo físico', detail: 'Registrar cantidad real por producto' },
                { label: 'Comparar', detail: 'stock_fisico vs stock_sistema' },
                { label: 'Finalizar inventario', detail: 'Ajusta stock a valores reales' }
              ],
              layout: 'vertical'
            }
          }
        ]
      },
      {
        id: 'flujo-stock',
        title: 'Flujo de movimiento de stock',
        path: '/stock',
        sections: [
          {
            title: 'Descripción',
            content: 'Traslados entre bodegas o sucursales. El stock se descuenta del origen y se suma al destino. Útil para reabastecer piso de venta desde bodega.'
          },
          {
            title: 'Diagrama del flujo',
            flow: {
              steps: [
                { label: 'Origen', detail: 'Bodega/sucursal de salida' },
                { label: 'Destino', detail: 'Bodega/sucursal de llegada' },
                { label: 'Validar stock', detail: 'Verificar cantidad disponible' },
                { label: 'Descontar origen', detail: 'stock -= cantidad' },
                { label: 'Sumar destino', detail: 'stock += cantidad' }
              ],
              layout: 'vertical'
            }
          }
        ]
      },
      {
        id: 'flujo-usuarios-colaboradores',
        title: 'Alta de usuarios y colaboradores',
        path: '/colaboradores',
        sections: [
          {
            title: 'Descripción',
            content:
              'En el ERP hay dos conceptos: la cuenta de usuario (login en Usuarios) y la ficha de colaborador (datos laborales en Colaboradores). Para personal que trabaja en local y necesita entrar al sistema, lo habitual es dar el alta una sola vez desde Colaboradores: ahí se crean automáticamente la cuenta y la ficha enlazadas.'
          },
          {
            title: 'Usuario vs colaborador',
            content:
              'Usuario: email, contraseña, nombre y rol (permisos). Sirve para iniciar sesión. Colaborador: RUT, teléfono, dirección, contrato, salario, sucursal, etc. Cada colaborador tiene exactamente un usuario vinculado (user_id). Puede existir un usuario sin colaborador (por ejemplo cuentas solo administrativas o de prueba); no todo usuario es empleado con ficha.'
          },
          {
            title: 'Flujo recomendado (empleado nuevo)',
            flow: {
              steps: [
                { label: 'Ir a Colaboradores', detail: 'Menú Recursos Humanos → Colaboradores' },
                { label: 'Nuevo Colaborador', detail: 'Completa datos personales, laborales y rol' },
                { label: 'Contraseña inicial', detail: 'Define la contraseña de acceso al ERP' },
                { label: 'Guardar', detail: 'El sistema crea usuario + colaborador en una sola operación' },
                { label: 'Verificación', detail: 'El mismo correo aparece en Usuarios y el personal en Colaboradores' }
              ],
              layout: 'vertical'
            }
          },
          {
            title: 'Flujo alternativo (solo cuenta, sin ficha)',
            flow: {
              steps: [
                { label: 'Ir a Usuarios', detail: 'Menú Administración → Usuarios' },
                { label: 'Nuevo usuario', detail: 'Nombre, email, contraseña y rol' },
                { label: 'Uso', detail: 'Ideal si no necesita datos de empleado en Colaboradores' }
              ],
              layout: 'vertical'
            }
          },
          {
            title: 'Evitar errores comunes',
            content:
              'No cree primero el mismo correo en Usuarios y después intente “Nuevo Colaborador” con ese mismo email: el alta de colaborador crea también el usuario y fallará por correo duplicado. Elija solo una ruta: empleado completo → Colaboradores; solo cuenta → Usuarios. Si ya existe el usuario y debe tener ficha, puede requerirse vincularlo por medio técnico (API link); consulte soporte.'
          }
        ]
      }
    ]
  },
  {
    id: 'inventario',
    title: 'Inventario',
    icon: 'Package',
    modules: [
      {
        id: 'productos',
        title: 'Productos',
        path: '/products',
        sections: [
          {
            title: 'Descripción',
            content: 'Gestión del catálogo de productos del minimarket. Permite crear, editar y dar de baja productos con sus precios, códigos de barra, stock y categorías.'
          },
          {
            title: 'Funcionalidades principales',
            content: 'Búsqueda y filtrado por nombre, categoría o código. Importación masiva desde Excel. Asignación de imágenes. Gestión de precios de venta y costo. Control de stock mínimo.'
          }
        ]
      },
      {
        id: 'categorias',
        title: 'Categorías',
        path: '/categories',
        sections: [
          {
            title: 'Descripción',
            content: 'Organización jerárquica de productos en categorías y subcategorías. Facilita la navegación y el reporte por tipo de producto.'
          },
          {
            title: 'Estructura',
            content: 'Las categorías pueden tener subcategorías. Los productos se asignan a una subcategoría. Permite mover productos entre categorías de forma masiva.'
          }
        ]
      },
      {
        id: 'inventarios',
        title: 'Inventarios',
        path: '/inventarios',
        sections: [
          {
            title: 'Descripción',
            content: 'Registro de inventarios físicos por bodega. Permite conciliar el stock real con el sistema mediante conteos y ajustes.'
          },
          {
            title: 'Proceso',
            content: 'Se crea un inventario por bodega, el sistema copia el stock actual. Se registra el conteo físico por producto y al finalizar se ajustan las diferencias.',
            flow: {
              steps: [
                'Crear inventario (bodega)',
                'Snapshot stock sistema',
                'Registrar conteo físico',
                'Finalizar → Ajustar stock'
              ],
              layout: 'horizontal'
            }
          }
        ]
      },
      {
        id: 'stock',
        title: 'Stock',
        path: '/stock',
        sections: [
          {
            title: 'Descripción',
            content: 'Visualización y control del inventario por producto y bodega. Muestra cantidades disponibles, reservadas y en movimiento.'
          },
          {
            title: 'Ajustes',
            content: 'Los ajustes de stock se realizan mediante inventarios o movimientos de compra/venta. El stock se actualiza automáticamente al registrar ventas o compras.'
          }
        ]
      },
      {
        id: 'vencimientos',
        title: 'Vencimientos',
        path: '/vencimientos',
        sections: [
          {
            title: 'Descripción',
            content: 'Control de productos próximos a vencer. Permite identificar lotes con fecha de vencimiento cercana para priorizar su venta o descarte.'
          },
          {
            title: 'Alertas',
            content: 'Configure el rango de días para visualizar productos en riesgo. Útil para cumplir normativas y reducir mermas por vencimiento.'
          }
        ]
      }
    ]
  },
  {
    id: 'ventas',
    title: 'Ventas',
    icon: 'ShoppingCart',
    modules: [
      {
        id: 'pos',
        title: 'Punto de Venta (POS)',
        path: '/pos',
        sections: [
          {
            title: 'Descripción',
            content: 'Interfaz principal para registrar ventas en caja. Permite buscar productos por código o nombre, agregar cantidades y procesar el pago.'
          },
          {
            title: 'Flujo de venta',
            content: 'El cajero debe tener caja abierta. Luego agrega productos, aplica promociones si corresponde, y procesa el pago. La venta descuenta stock automáticamente.',
            flow: {
              steps: [
                'Abrir caja (monto inicial)',
                'Buscar y agregar productos',
                'Aplicar promociones (opcional)',
                'Procesar pago',
                'Venta registrada → Stock descontado'
              ],
              layout: 'horizontal'
            }
          }
        ]
      },
      {
        id: 'preventas',
        title: 'Preventas',
        path: '/preventas',
        sections: [
          {
            title: 'Descripción',
            content: 'Las preventas son pedidos en piso antes del cobro en caja. Estados: pendiente (estado=true) y cerrada (estado=false, ya cobrada). No reservan stock hasta completar la venta.'
          },
          {
            title: 'Flujo Preventa → Venta',
            content: 'Al completar el pago en POS, la preventa pasa a cerrada y se genera la Venta final vinculada en venta_preventa_link. El detalle incluye productos, promociones y ventas por peso.'
          },
          {
            title: 'Historial',
            content: 'En Gestión de Preventas puede filtrar pendientes, cerradas o todas. Las cerradas muestran el ID de venta asociado.'
          }
        ]
      },
      {
        id: 'ordenes-online',
        title: 'Ventas Online',
        path: '/ordenes-online',
        sections: [
          {
            title: 'Descripción',
            content: 'Listado de órdenes del ecommerce (proyecto ventasonline). Comparte la misma base PostgreSQL. Estados: pendiente_pago, pagada, despachada, cancelada. Requiere permiso preventas_ver.'
          },
          {
            title: 'Estados de la orden',
            content: 'Pagadas: listas para preparar y entregar. Pendientes de pago: checkout iniciado sin confirmar pago. Despachadas: ya entregadas al cliente. Canceladas: anuladas (si estaban pagadas, el stock vuelve a bodega ecommerce).'
          },
          {
            title: 'Relación con Preventas',
            content: 'Cada orden pagada genera una preventa PRV con origen venta_online. El cliente ya pagó en la web — en caja solo se verifica y entrega, sin cobrar de nuevo. Use el código PRV para cruzar con Gestión de Preventas o cargar en POS si aplica.'
          }
        ]
      },
      {
        id: 'ventas',
        title: 'Historial de Ventas',
        path: '/ventas',
        sections: [
          {
            title: 'Descripción',
            content: 'Registro histórico de todas las ventas realizadas. Permite consultar por fecha, cajero, sucursal o documento.'
          },
          {
            title: 'Documentos',
            content: 'El sistema soporta Boleta y Factura. El IVA chileno (19%) se calcula automáticamente: monto_neto = sub_total / 1.19.'
          }
        ]
      },
      {
        id: 'cajas',
        title: 'Cajas',
        path: '/cajas',
        sections: [
          {
            title: 'Descripción',
            content: 'Gestión de apertura y cierre de caja. Cada cajero debe abrir su caja al iniciar el turno y cerrarla al finalizar.'
          },
          {
            title: 'Apertura y cierre',
            content: 'Al abrir se registra el monto inicial. Todas las ventas del turno quedan asociadas a esa apertura. Al cerrar se concilia efectivo, transferencia y tarjeta.',
            flow: {
              steps: [
                'Apertura (monto inicial)',
                'Ventas del turno',
                'Cierre (arqueo por medio de pago)'
              ],
              layout: 'horizontal'
            }
          }
        ]
      }
    ]
  },
  {
    id: 'finanzas',
    title: 'Finanzas',
    icon: 'Wallet',
    modules: [
      {
        id: 'reportes',
        title: 'Reportes',
        path: '/reports',
        sections: [
          {
            title: 'Descripción',
            content: 'Totales por día operacional con desglose por medio de pago, listado de cierres de turno con horario real, top productos y ranking de pre-vendedores. Permite filtrar por rango de fechas y exportar tanto totales diarios como cierres de turno a CSV.'
          },
          {
            title: 'Día operacional vs día calendario',
            content: 'Los totales no se agrupan por DATE(created_at) sino por fecha_operacional. Para turnos que abren a las 08:00 y cierran a las 03:00 del día siguiente, todas las ventas (incluidas las de madrugada antes del corte 04:00) se cuentan en el día de apertura. Esto evita que un mismo turno aparezca partido en dos buckets distintos.'
          },
          {
            title: 'Métricas disponibles',
            content: 'Ventas por día, ventas por producto, ingresos mensuales, comparativas de períodos. Filtros por sucursal y rango de fechas.'
          }
        ]
      },
      {
        id: 'flujo-caja',
        title: 'Flujo de Caja',
        path: '/flujo-caja',
        sections: [
          {
            title: 'Descripción',
            content: 'Resumen de ingresos y egresos del negocio (utilidad). Las inyecciones y retiros del POS se ven aparte, en la pestaña Cajón POS.'
          },
          {
            title: 'Uso',
            content: 'Filtre por período. Dashboard y Movimientos son gastos/ingresos contables. Cajón POS lista inyecciones y retiros de todas las cajas, con responsable, desglose de billetes e impresión de comprobante.'
          }
        ]
      }
    ]
  },
  {
    id: 'compras',
    title: 'Compras',
    icon: 'Package',
    modules: [
      {
        id: 'compras',
        title: 'Compras',
        path: '/compras',
        sections: [
          {
            title: 'Descripción',
            content: 'Registro de órdenes de compra a proveedores. Al recibir mercadería se actualiza el stock automáticamente.'
          },
          {
            title: 'Proceso',
            content: 'Seleccione proveedor, bodega y productos. Ingrese cantidad y costo. En facturas de licores use Neto: el Valor de cada línea, más flete. El ILA (18% / 20,5% / 31,5%) solo aplica a los productos marcados (licores, cervezas, vinos); el flete y el resto no. El IVA es 19% sobre el neto. El PPP usa el costo final pagado. El precio queda pendiente de autorización del Administrador.',
            flow: {
              steps: [
                'Proveedor + Bodega',
                'Productos + cantidad + costo + % ganancia',
                'Registrar compra',
                'PPP + precio pendiente de autorización'
              ],
              layout: 'horizontal'
            }
          },
          {
            title: 'Autorización y bloqueo',
            content: 'Hasta autorizar, se vende al precio anterior mientras quede stock previo. Se bloquea al agotar ese stock o a las 24 horas. Bandeja: Precios pendientes. El Administrador autoriza con su usuario y contraseña.'
          },
        ]
      },
      {
        id: 'precios-pendientes',
        title: 'Precios pendientes',
        path: '/precios-pendientes',
        sections: [
          {
            title: 'Descripción',
            content: 'Bandeja de precios de venta y cambios de margen mínimo que esperan autorización del Administrador.'
          },
          {
            title: 'Uso',
            content: 'El Administrador ingresa su usuario y contraseña. Al autorizar, el producto se desbloquea y queda activo con el nuevo precio. Plazo: 24 horas desde la factura.'
          }
        ]
      },
      {
        id: 'proveedores',
        title: 'Proveedores',
        path: '/proveedores',
        sections: [
          {
            title: 'Descripción',
            content: 'Catálogo de proveedores con datos de contacto, RUT y productos asociados.'
          },
          {
            title: 'Gestión',
            content: 'Crear, editar y dar de baja proveedores. Asociar productos a cada proveedor para facilitar las órdenes de compra.'
          }
        ]
      }
    ]
  },
  {
    id: 'promociones',
    title: 'Promociones',
    icon: 'TrendingUp',
    modules: [
      {
        id: 'promociones',
        title: 'Promociones',
        path: '/promociones',
        sections: [
          {
            title: 'Descripción',
            content: 'Configuración de descuentos y ofertas por producto o categoría. Las promociones se aplican automáticamente en el POS.'
          },
          {
            title: 'Tipos',
            content: 'Descuento por porcentaje, precio especial, 2x1, etc. Configure fechas de vigencia para cada promoción.'
          }
        ]
      }
    ]
  },
  {
    id: 'rrhh',
    title: 'Recursos Humanos',
    icon: 'Users',
    modules: [
      {
        id: 'colaboradores',
        title: 'Colaboradores',
        path: '/colaboradores',
        sections: [
          {
            title: 'Descripción',
            content: 'Gestión de empleados del minimarket. Cada colaborador tiene un usuario para acceder al sistema. Al usar “Nuevo Colaborador”, el sistema crea en el mismo paso la cuenta de usuario y la ficha laboral vinculada.'
          },
          {
            title: 'Cómo dar de alta a un empleado',
            content: 'Use Nuevo Colaborador: complete nombre, email único, contraseña inicial, rol, y datos opcionales (RUT, teléfono, dirección, fecha de contratación, salario, sucursal). No hace falta crear el mismo correo antes en Usuarios. Tras guardar, la persona puede iniciar sesión con ese email.'
          },
          {
            title: 'Datos para liquidación',
            content: 'Complete también AFP, comisión, salud (Fonasa o Isapre), tipo de contrato, colación, movilización y jornada semanal. Esos campos alimentan el cálculo de Liquidaciones de sueldo.'
          },
          {
            title: 'Permisos',
            content: 'Los colaboradores se asignan a roles que definen qué módulos pueden ver y editar. El administrador gestiona accesos; el rol se elige en el formulario del colaborador.'
          },
          {
            title: 'Ayuda relacionada',
            content: 'En Ayuda → Flujos de negocio → “Alta de usuarios y colaboradores” encontrará el diagrama recomendado y cómo evitar duplicar cuentas.'
          }
        ]
      },
      {
        id: 'roles',
        title: 'Roles y Permisos',
        path: '/roles',
        sections: [
          {
            title: 'Descripción',
            content: 'Definición de roles con permisos granulares. Ej: Cajero (solo POS y caja), Bodeguero (inventario), Admin (todo).'
          },
          {
            title: 'Permisos disponibles',
            content: 'productos_ver/editar/eliminar, categorias_ver/editar, pos_ver, reportes_ver, cajas_ver, compras_ver, etc. Combine según el perfil del cargo.'
          }
        ]
      },
      {
        id: 'agenda',
        title: 'Agenda de Turnos',
        path: '/agenda',
        sections: [
          {
            title: 'Descripción',
            content: 'Calendario de turnos por colaborador. Permite planificar horarios y visualizar quién trabaja cada día.'
          },
          {
            title: 'Uso',
            content: 'Asigne turnos por fecha y colaborador. Útil para coordinación de cajas y cobertura de horarios.'
          }
        ]
      },
      {
        id: 'liquidaciones',
        title: 'Liquidaciones de sueldo',
        path: '/liquidaciones',
        sections: [
          {
            title: 'Descripción',
            content: 'Genera la liquidación mensual de cada colaborador a partir del sueldo base, turnos del periodo, horas extras aprobadas y haberes o descuentos extra (bonos, anticipos). El cálculo incluye gratificación legal art. 50, AFP, salud, seguro de cesantía e impuesto único de 2da categoría según UTM.'
          },
          {
            title: 'Cómo emitir el mes',
            flow: {
              steps: [
                { label: 'Completar fichas', detail: 'En Colaboradores: salario, AFP, salud, colación, contrato' },
                { label: 'Registrar horas extras', detail: 'Pestaña Horas extras del periodo' },
                { label: 'Cargar anticipos o bonos', detail: 'Pestaña Haberes y descuentos' },
                { label: 'Actualizar UTM/UF', detail: 'Pestaña Parámetros, valores del mes' },
                { label: 'Generar periodo', detail: 'Crea borradores para todos los activos' },
                { label: 'Revisar e imprimir', detail: 'Emitir bloquea HE; marcar pagada al transferir' }
              ]
            }
          },
          {
            title: 'Horas extras',
            content: 'Se valorizan con recargo del 50% (o 100% si se indica) sobre el valor hora ordinaria: sueldo base / 30 / (jornada semanal ÷ días de la semana). Por defecto la jornada es 42 horas (Ley 21.561 vigente desde abril 2026).'
          },
          {
            title: 'Alcance',
            content: 'Es un módulo operativo interno. No declara cotizaciones en Previred ni reemplaza al contador. Revise los parámetros IMM, UTM y UF antes de cada cierre mensual.'
          }
        ]
      }
    ]
  },
  {
    id: 'administracion',
    title: 'Administración',
    icon: 'Building2',
    modules: [
      {
        id: 'users',
        title: 'Usuarios',
        path: '/users',
        sections: [
          {
            title: 'Descripción',
            content: 'Gestión de cuentas de acceso al sistema (email, contraseña, nombre, rol). Un usuario puede existir sin ser colaborador. Si la persona es empleado con ficha laboral, lo recomendable es crearla desde Colaboradores para no duplicar el correo.'
          },
          {
            title: 'Cuándo usar solo “Nuevo usuario”',
            content: 'Use este módulo cuando necesite una cuenta de acceso sin registrar datos de empleado (RUT, contrato, sucursal en ficha). Ejemplos: usuario de prueba, cuenta de servicio o perfiles que no pasan por Colaboradores.'
          },
          {
            title: 'Configuración',
            content: 'Crear usuarios con email y contraseña. Asignar rol para definir permisos. Editar o desactivar según política del negocio. Los colaboradores también pueden tener su contraseña restablecida desde el módulo Colaboradores.'
          },
          {
            title: 'Ayuda relacionada',
            content: 'Vea en Ayuda → Flujos de negocio el apartado “Alta de usuarios y colaboradores” para la relación entre ambos módulos y el orden recomendado.'
          }
        ]
      },
      {
        id: 'usuarios-activos',
        title: 'Usuarios Activos',
        path: '/usuarios-activos',
        sections: [
          {
            title: 'Descripción',
            content: 'Visualización de sesiones activas en el sistema. Permite ver quién está conectado y desde qué dispositivo.'
          },
          {
            title: 'Seguridad',
            content: 'Útil para detectar sesiones no autorizadas o cerrar sesiones remotas si es necesario.'
          }
        ]
      },
      {
        id: 'sucursales',
        title: 'Sucursales',
        path: '/sucursales',
        sections: [
          {
            title: 'Descripción',
            content: 'Configuración de sucursales o locales del negocio. Cada sucursal puede tener sus propias cajas y reportes.'
          },
          {
            title: 'Multi-sucursal',
            content: 'Los reportes y ventas pueden filtrarse por sucursal. Útil para negocios con múltiples puntos de venta.'
          }
        ]
      },
      {
        id: 'bodegas',
        title: 'Bodegas',
        path: '/bodegas',
        sections: [
          {
            title: 'Descripción',
            content: 'Gestión de bodegas o depósitos. El stock se puede distribuir entre bodegas (ej: bodega principal, piso de venta).'
          },
          {
            title: 'Inventarios',
            content: 'Los inventarios físicos se realizan por bodega. Permite conciliar el stock real con el sistema.'
          }
        ]
      }
    ]
  }
];
